import { Equipment } from "../../models/equipment";
import { EquipmentTrainerState } from "./equipmentTrainerSlice";

export function calcTrainerResults(state: EquipmentTrainerState) {
    const wielded = getEqInSlot(state, 'wielded');

    let modOpponentDb = state.opponentDb;
    let modOpponentPb = state.opponentPb;
    if (state.opponentSat) {
      modOpponentDb *= (1/4);
      modOpponentPb *= (3/4);
    }
    
    state.ob = getCharOb(state);
    state.db = getCharDB(state);
    state.pb = getCharPb(state);
    state.abs = getCharAbs(state);
    state.totalWeight = round(getTotalWeight(state));
    state.damage = getCharDmg(state);
    state.mvs = getCharMvs(state);
    state.weaponWeight = wielded?.weight ?? 0;
    state.bashChance = getCharBashRate(state);
    state.opponentBashChance = getCharBashedRate(state);
    state.hitChance = Math.round(getHitRate(state.ob, modOpponentDb, modOpponentPb));
    state.opponentHitChance = Math.round(getHitRate(state.opponentOb, state.db, state.pb));
    state.kickChance = getKickProb(state.kick, state.ob, modOpponentDb);
    state.opponentKickChance = getKickProb(99, state.opponentOb, state.db);
    state.chargeChance = state.raceId !== 'trolloc' ? getChargeProb(state.level, state.charge, state.weapon, state.flying, state.opponentLevel, state.terrain) : 0;
    state.opponentChargeChance = getChargeProb(state.opponentLevel, 99, 99, false, state.level, state.terrain);
    state.stabChance = getStabProb(state.level, state.stab, state.weapon, state.hide, state.sneak, state.opponentLevel, state.opponentNotice);
    state.opponentStabChance = getStabProb(state.opponentLevel, 99, 99, 99, 99, state.level, state.notice);
    state.attackChance = round(getAttackProp(state.attack, state.dext));

    for (let damage of state.damage) {
      damage.mean = round(damage.mean);
      damage.variance = round(damage.variance);
      damage.stdev = round(damage.stdev);
    }
}

export function getEqInSlot(state: EquipmentTrainerState, slot: string) {
    let result: Equipment | undefined = undefined;
    const name = (state.worn as any)[slot];
    if (name) {
        result = state.equipment.find(x => x.name === name);
    }
    return result;
}

function getCharMvs(state: EquipmentTrainerState) {
  let result = 0;
  for (const key of Object.keys(state.worn).filter(x => !['wielded', 'shield'].includes(x))) {
      result += getEqInSlot(state, key)?.mvs ?? 0;
  }
  return Math.min(result, 20);  
}

function getTotalWeight(state: EquipmentTrainerState) {
    let result = state.weight;
    for (const key of Object.keys(state.worn)) {
        result += getEqInSlot(state, key)?.weight ?? 0;
    }
    return result;
}

function getMaxWeight(state: EquipmentTrainerState) {
    const maxWeight = 8.5 * state.strn + 3.5 * state.cont;
    return maxWeight;   
}

function getCharAbs(state: EquipmentTrainerState) {
  let abs = 0;
  const slot_abs: {[key:string]: number} = { 'head': 8, 'body': 50, 'arms': 14, 'hands': 4, 'legs': 20, 'feet': 4 };
  const keys = Object.keys(slot_abs);
  for (const key of keys) {
    const slotAbs = getEqInSlot(state, key)?.abs ?? 0;
    abs += slotAbs * slot_abs[key];
  }
  return Math.floor(abs / 100 + .5);
}

function getCharOb(state: EquipmentTrainerState) {
    const level = Math.min(state.level, 30);
    const postures_ob: {[key:string]: number} = { 'posture-none': 0, 'posture-defensive': 0, 'posture-normal': 3, 'posture-offensive': 5 };
    const positions_ob: {[key:string]: number} = { 'position-standing': 1, 'position-sitting': 1, 'position-resting': 1, 'position-sleeping': 1 };
    const level_mood_ob: {[key:string]: number} = { 'mood-wimpy': 7/3, 'mood-normal': 77/30, 'mood-brave': 161/60, 'mood-berserk': 35/12 };
    const wpn_mood_ob: {[key:string]: number} = { 'mood-wimpy': 1, 'mood-normal': 1.1, 'mood-brave': 1.15, 'mood-berserk': 1.25 };
    const weapon = getEqInSlot(state, "wielded");
    
    const hardenedApplied = weapon && state.weaponGrade === 'weapongrade-hardened';
    const blindImmunity = state.raceId === 'fade';
    const darknessImmunity = ["trolloc", "fade", "grayman"].includes(state.raceId);
    const blindApplied = state.blindWeave && !blindImmunity;
    const darknessApplied = !blindApplied && state.darknessWeave && !darknessImmunity;
  
    let ob = 0;
    ob += state.professionId === 'warrior' && !state.bonded ? 5 : 0;
    ob += level * level_mood_ob[state.mood];

    let weaponOb = weapon?.ob ?? 0;
    weaponOb += hardenedApplied ? 5 : 0;

    ob += (state.weapon / 100) * (state.strn / 19) * weaponOb * wpn_mood_ob[state.mood];
    ob += state.mounted ? Math.ceil(state.ride / 7) * ((state.ride + 4) * wpn_mood_ob[state.mood]) : 0;
    ob *= (1 - (5 * getTotalWeight(state)) / (25 * getMaxWeight(state)));
    ob += postures_ob[state.posture];
    ob += state.bonded ? 10 : 0;
    ob *= blindApplied ? 0.5 : 1;
    ob *= darknessApplied ? 0.9 : 1;
    ob *= state.chillWeave ? 0.9 : 1;
    ob *= positions_ob[state.position]; 
    ob = state.position === 'sleeping' ? 1 : ob;
    ob += 1;
    ob += state.raceId === 'trolloc' ? 4 : 0;
    ob = Math.floor(ob + 0.5);
    ob = Math.max(ob, 1);
    return ob;
}

function getCharDB(state: EquipmentTrainerState): number {
    const level = Math.min(state.level, 30);
    const positionsDB: {[key: string]: number} = { 'position-standing': 1, 'position-sitting': 1 / 4, 'position-resting': 1 / 4, 'position-sleeping': 1 };
    const shieldDb = getEqInSlot(state, 'shield')?.db ?? 0;
    const blindImmunity = state.raceId === 'fade';
    const darknessImmunity = ["trolloc", "fade", "grayman"].includes(state.raceId);
    const blindApplied = state.blindWeave && !blindImmunity;
    const darknessApplied = !blindApplied && state.darknessWeave && !darknessImmunity;
    const keys = Object.keys(state.worn).filter(x => !['wielded', 'shield'].includes(x));
    const eqDb = keys.reduce((acc, key) => acc + (getEqInSlot(state, key)?.db ?? 0), 0);

    let db = 0;
    db += (level * 2) / 3;
    db += (state.dodge / 99) * (state.dext / 19) * 70;
    db += shieldDb;
    db += state.mounted ? Math.ceil(state.ride / 7) * (state.ride - 11) : 0;
    db *= (1 - (5 * getTotalWeight(state)) / (8 * getMaxWeight(state)));
    db += eqDb;
    db *= blindApplied ? 0.8 : 1;
    db *= darknessApplied ? 0.9 : 1;
    db += state.armorWeave ? 5 : 0;
    db *= state.contagionWeave ? 0.8 : 1;
    db *= positionsDB[state.position];
    db = state.position === 'sleeping' ? 1 : db;
    db += state.weaving ? -10 : 0;
    db = Math.floor(db + 0.5);
    db = Math.max(db, 1);
    return db;
  }

function getCharPb(state: EquipmentTrainerState) {
    const level = Math.min(state.level, 30);
    const postures_pb: {[key:string]: number} = { 'posture-none': 0, 'posture-defensive': 5, 'posture-normal': 2, 'posture-offensive': 0 };
    const positions_pb: {[key:string]: number} = { 'position-standing': 1, 'position-sitting': 3 / 4, 'position-resting': 3 / 4, 'position-sleeping': 1 / 2 };
    const mood_pb: {[key:string]: number} = { 'mood-wimpy': 71 / 50, 'mood-normal': 13 / 10, 'mood-brave': 23 / 20, 'mood-berserk': 1 };
    const wpn_pb = getEqInSlot(state, "wielded")?.pb ?? 0;
    const shield_pb = getEqInSlot(state, "shield")?.pb ?? 0;
    const blindImmunity = state.raceId === 'fade';
    const darknessImmunity = ["trolloc", "fade", "grayman"].includes(state.raceId);
    const blindApplied = state.blindWeave && !blindImmunity;
    const darknessApplied = !blindApplied && state.darknessWeave && !darknessImmunity;
    const keys = Object.keys(state.worn).filter(x => !['wielded', 'shield'].includes(x));
    const eqPb = keys.reduce((acc, key) => acc + (getEqInSlot(state, key)?.pb ?? 0), 0);
    
    let pb = 0;
    let lvl_pb = Math.min(level, 20);
    pb += lvl_pb * mood_pb[state.mood];
    pb += (state.shield / 100) * (state.dext / 19) * shield_pb * mood_pb[state.mood];
    pb += (state.weapon / 100) * (state.strn / 19) * wpn_pb * mood_pb[state.mood];
    pb *= (1 - (5 * getTotalWeight(state)) / (16 * getMaxWeight(state)));
    pb += eqPb * mood_pb[state.mood];
    pb += postures_pb[state.posture];
    pb *= blindApplied ? 0.5 : 1;
    pb *= darknessApplied ? 0.75 : 1;
    pb *= state.contagionWeave ? 0.5 : 1;
    pb *= positions_pb[state.position];
    pb = Math.floor(pb + 0.5);
    pb = Math.max(pb, 1);
    return pb;
}

function getCharBashRate(state: EquipmentTrainerState): number {
  const wpn = getEqInSlot(state, "wielded");
  const shield = getEqInSlot(state, "shield");
  const wpnType = wpn?.weaponType  ?? '';
  const wpnWeight = wpn?.weight ?? 0;

  if (!wpn && !shield) {
    return 0;
  }

  const ob = state.ob;
  const pb = state.pb;
  const db = state.opponentDb;  
  const club = wpnType === "club";

  let bashChance = 30;

  if (wpnWeight > 10) {
    bashChance +=
      wpnWeight / 5 * (ob / 3) + pb / 4 - (db * 3) / 2 > 30
        ? 30
        : wpnWeight / 5 * (ob / 3) + pb / 4 - (db * 3) / 2;
  } else {
    bashChance +=
      ob / 2 + pb / 4 - (db * 3) / 2 > 20
        ? 20
        : ob / 2 + pb / 4 - (db * 3) / 2;
  }
  bashChance = Math.floor(bashChance);

  if (state.mounted) {
    bashChance += 5;
  }

  bashChance = Math.floor((bashChance * state.bash) / 99);

  if (bashChance < 0) {
    bashChance = 0;
  }

  if (club) {
    bashChance = Math.floor(bashChance + state.weapon / 10);
  }

  if (bashChance > 95) {
    bashChance = 95;
  }

  return bashChance;
}

function getCharBashedRate(state: EquipmentTrainerState): number {
  const wpnWeight = state.opponentWeaponWeight;
  const ob = state.opponentOb;
  const pb = state.opponentPb;
  const db = state.db;
  const club = state.opponentClubs;
  const mounted = true;
  const weapon = 99;
  const bash = 99;

  let bashChance = 30;

  if (wpnWeight > 10) {
    bashChance +=
      wpnWeight / 5 * (ob / 3) + pb / 4 - (db * 3) / 2 > 30
        ? 30
        : wpnWeight / 5 * (ob / 3) + pb / 4 - (db * 3) / 2;
  } else {
    bashChance +=
      ob / 2 + pb / 4 - (db * 3) / 2 > 20
        ? 20
        : ob / 2 + pb / 4 - (db * 3) / 2;
  }
  bashChance = Math.floor(bashChance);

  if (mounted) {
    bashChance += 5;
  }

  bashChance = Math.floor((bashChance * bash) / 99);

  if (bashChance < 0) {
    bashChance = 0;
  }

  if (club) {
    bashChance = Math.floor(bashChance + weapon / 10);
  }

  if (bashChance > 95) {
    bashChance = 95;
  }

  return bashChance;
}

function getCharDmg(state: EquipmentTrainerState): any[] {
  const results = [] as {
    name: string;
    damage: string;
    mean: number;
    variance: number;
    stdev: number;
  }[];
  
  //base damage
  const weapon = getEqInSlot(state, 'wielded');
  const shield = getEqInSlot(state, 'shield');
  let finalX: number;
  let finalY: number;
  if (weapon) {
    const match = weapon.damage.match(/(\d+)d(\d+)/)!;
    const x = finalX = parseInt(match[1], 10);
    const y = finalY = parseInt(match[2], 10);
    const name = 'base';
    const damage = `${x}d${y}`;
    const mean = (x * (y + 1)) / 2;
    const variance = (x * (y * y - 1)) / 12;
    const stdev = Math.sqrt(variance);
    results.push({name, damage, mean, variance, stdev});
  } else {
    const x = finalX = 2;
    const y = finalY = Math.max(Math.floor(Math.min(state.level, 30) / 5), 1);
    const name = 'base';
    const damage = `${x}d${y}`;
    const mean = (x * (y + 1)) / 2;
    const variance = (x * (y * y - 1)) / 12;
    const stdev = Math.sqrt(variance);
    results.push({name, damage, mean, variance, stdev});
  }

  //spear/lance damage
  const spear = weapon && weapon.weaponType === 'spear' && state.mounted;
  const lance = weapon && weapon.weaponType === 'lance' && state.mounted;
  if (spear || lance) {
    finalX++;
    const x = 1;
    const y = finalY;
    const name = spear ? 'spear' : 'lance';
    const damage = `${x}d${y}`;
    const mean = (x * (y + 1)) / 2;
    const variance = (x * (y * y - 1)) / 12;
    const stdev = Math.sqrt(variance);
    results.push({name, damage, mean, variance, stdev});
  }
  
  //1.5 hands damage
  const hands = weapon && weapon.weaponHands === 1.5 && !shield;
  if (hands && !lance && !spear) {
    finalX++;
    const x = 1;
    const y = finalY;
    const name = '1.5h';
    const damage = `${x}d${y}`;
    const mean = (x * (y + 1)) / 2;
    const variance = (x * (y * y - 1)) / 12;
    const stdev = Math.sqrt(variance);
    results.push({name, damage, mean, variance, stdev});
  }

  //honed
  const honed = weapon && state.weaponGrade !== 'weapongrade-basic';
  if (honed) {
    const x = 2;
    const y = 4;
    const name = 'honed';
    const damage = `${x}d${y}`;
    const mean = (x * (y + 1)) / 2;
    const variance = (x * (y * y - 1)) / 12;
    const stdev = Math.sqrt(variance);
    results.push({name, damage, mean, variance, stdev});
  }

  //rank
  const remort = ['fade', 'dreadlord', 'grayman'].includes(state.raceId);
  const rank = remort ? Math.min(Math.max(state.rank + 3, 7), 10) : state.rank;
  if (rank >= 2) {
    const x = Math.floor(rank / 2);
    const name = 'rank';
    const damage = x.toString();
    const mean = x;
    const variance = 0;
    const stdev = 0;
    results.push({name, damage, mean, variance, stdev});
  }

  //strength
  const strength = state.strn;
  if (strength >= 15) {
    const max = 2 * (strength - 14);
    const name = 'strength';
    const damage = `0-${max}`;
    const mean = ((strength - 14) * (strength - 13)) / 14;
    const variance = (strength - 14) * (strength - 13) * (2 * strength - 27) / 42;
    const stdev = Math.sqrt(variance);
    results.push({name, damage, mean, variance, stdev});
  }

  //total
  const totalName = 'total';
  const totalDamage = `${finalX}d${finalY}` + (results.length > 1 ? '*' : '');
  const totalMean = results.reduce((acc, val) => acc + val.mean, 0);
  const totalVariance = results.reduce((acc, val) => acc + val.variance, 0);
  const totalStdev = Math.sqrt(totalVariance);
  results.unshift({
    name: totalName,
    damage: totalDamage,
    mean: totalMean,
    variance: totalVariance,
    stdev: totalStdev
  });

  return results;
}

function round(value: number) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function getHitRate(ob: number, db: number, pb: number): number {
  let hits = 0;
  for (let i = 1; i <= 120; i++) {
    for (let j = 1; j <= 100; j++) {
      if (ob + i >  db + pb + j) {
        hits++;
      }
    }
  }
  return hits / (120 * 100) * 100;
}

function getKickProb(attackerKick: number, attackerOb: number, victimDb: number) {
    let result = 0;
    if (attackerKick >= 15) {
      result = attackerKick / 2 + attackerOb / 3 - victimDb / 2;
      result = Math.min(Math.max(Math.trunc(result), 0), 99);
    }
    return result;
}
 
function getChargeProb(
  attackerLevel: number,
  attackerCharge: number,
  attackerWeapon: number,
  attackerFlying: boolean,
  victimLevel: number,
  terrain: string
) {
  let result = 0;
  const levelDiff = Math.min(30, attackerLevel) - Math.min(30, victimLevel);
  result = Math.trunc(Math.max(2 * levelDiff + 55, 0) * attackerCharge / 99);
  result = Math.trunc(result * attackerWeapon / 99);   
  if (attackerFlying) {
    result /= 2;
  } else if (terrain === 'terrain-forest') {
    result -= 15;
  } else if (terrain === 'terrain-hillforest') {
    result -= 25;
  } else if (terrain === 'terrain-mountain') {
    result -= 75;
  }
  result = Math.min(Math.max(Math.trunc(result), 0), 99);
  return result;
}

function getStabProb(
  attackerLevel: number,
  attackerStab: number,
  attackerWeapon: number,
  attackerHide: number,
  attackerSneak: number,
  victimLevel: number,
  victimNotice: number
) {
  let result = 0
  const levelDiff = Math.min(30, attackerLevel) - Math.min(30, victimLevel);
  result += Math.max(2 * levelDiff + 55, 0) * attackerStab / 99;
  result = Math.trunc(result * attackerWeapon / 99);
  result -= victimNotice / 5;
  result *= attackerHide / 99;
  result += Math.max(0, (attackerSneak - 50) / 10);
  result = Math.min(Math.max(Math.trunc(result), 0), 99);
  return result;
}

function getAttackProp(attack: number, dex: number)
{
  return 1 + 12 / 14 * (2 * dex + Math.floor(attack/2) - 32) / 69;
}
